import type { ClassValue } from 'clsx';
import { inRange } from 'lodash-es';
import { useMemo, useRef } from 'react';

import { VFlex } from '../VFlex';
import { joinCn } from '../util';
import { useVSliderContext } from './hook';
import type { VSliderNormalizedMarkerItem } from './type';
import { createSliderValueUpdater } from './util';

type VSliderMarkerContainerProps = {
  markerClassName?: (value: number, isInRange: boolean) => ClassValue;
  onMarkerClick?(value: number): void;
};

export const VSliderMarkerContainer = (props: VSliderMarkerContainerProps) => {
  const {
    disabled,
    normalizedMarkerItemMap,
    normalizedValue,
    marker,
    min,
    readOnly,
    style,
    type,
    setValue,
  } = useVSliderContext();
  const $marker = useRef<HTMLDivElement>(null);
  const selectedValueSet = useMemo(
    () => new Set(normalizedValue.flat()),
    [normalizedValue],
  );
  const changeValue = createSliderValueUpdater({
    normalizedValue,
    min,
    type,
    setValue,
  });

  const isSelectedValue = (value: number) => selectedValueSet.has(value);

  const onMarkerClick = (value: number) => {
    if (disabled || readOnly) return;

    if (typeof props.onMarkerClick === 'function') {
      props.onMarkerClick(value);

      return;
    }

    changeValue(value);
  };

  return (
    <div
      aria-label="v-slider-marker-container"
      className={style.markerContainer}
      ref={$marker}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        if (
          target.ariaValueText != null &&
          target.ariaValueText !== '' &&
          !isNaN(+target.ariaValueText)
        ) {
          onMarkerClick(+target.ariaValueText);
        }
      }}
    >
      {marker &&
        normalizedMarkerItemMap &&
        Object.values(normalizedMarkerItemMap).map(row => {
          if (row == null || row.hidden) return null;

          const isSelected = isSelectedValue(row.value);
          const isInRange = normalizedValue.some(value =>
            inRange(row.value, value[0], value[1] + 1),
          );

          return (
            <VSliderMarker
              key={row.value}
              className={joinCn(props.markerClassName?.(row.value, isInRange))}
              isSelected={isSelected}
              {...row}
            />
          );
        })}
    </div>
  );
};

const VSliderMarker = ({
  className,
  isSelected,
  ...row
}: VSliderNormalizedMarkerItem & {
  className?: ClassValue;
  isSelected: boolean;
}) => {
  const { disabled, readOnly } = useVSliderContext();

  return (
    <VFlex
      aria-disabled={disabled}
      aria-label="v-slider-marker"
      aria-readonly={readOnly}
      aria-selected={isSelected}
      aria-valuetext={`${row.value}`}
      className={joinCn(className)}
      items="center"
      justify="center"
      style={{ left: `${row.left.marker}px` }}
    >
      {typeof row.label === 'string' ? row.label : row.label(row.value)}
    </VFlex>
  );
};

VSliderMarkerContainer.displayName = 'VSliderMarkerContainer';
VSliderMarker.displayName = 'VSliderMarker';
